import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTreeCached, clearTreeCache, type TreeItem } from '../../api/treeCache';
import { refToSegment } from '../../utils/refSegment';
import './RepoFileTree.css';

export type { TreeItem };

interface RepoFileTreeProps {
    repoPath: string;
    refName: string;
    /** 当前树路径（目录），用于展开与高亮 */
    treePath: string;
    /** 当前文件路径（Blob 页），用于高亮 */
    filePath?: string;
}

export function RepoFileTree({ repoPath, refName, treePath, filePath }: RepoFileTreeProps) {
    const navigate = useNavigate();
    const [rootItems, setRootItems] = useState<TreeItem[]>([]);
    /** 已加载的目录 children，折叠不删除，用于会话内缓存 */
    const [loadedChildren, setLoadedChildren] = useState<Record<string, TreeItem[]>>({});
    /** 当前展开的 path 集合（仅 UI 状态） */
    const [expandedPaths, setExpandedPaths] = useState<Record<string, true>>({});
    const [loading, setLoading] = useState(true);

    const loadChildren = useCallback(
        async (path: string): Promise<TreeItem[]> => {
            const data = await getTreeCached(repoPath, refName, path);
            return data.items || [];
        },
        [repoPath, refName]
    );

    // 根目录请求；仅 repoPath/refName 变化时清空会话缓存并重新加载根（同 repo+ref 跨页面保留缓存）
    const prevRepoRef = useRef({ repoPath: '', refName: '' });
    useEffect(() => {
        let cancelled = false;
        const prev = prevRepoRef.current;
        const repoRefChanged =
            (prev.repoPath && prev.refName) &&
            (prev.repoPath !== repoPath || prev.refName !== refName);
        if (repoRefChanged) clearTreeCache(repoPath, refName);
        prevRepoRef.current = { repoPath, refName };
        setLoadedChildren({});
        setExpandedPaths({});
        (async () => {
            try {
                setLoading(true);
                const data = await getTreeCached(repoPath, refName, '');
                if (!cancelled) setRootItems(data.items || []);
            } catch {
                if (!cancelled) setRootItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [repoPath, refName]);

    // 根据 URL 展开祖先目录：并行请求各层（走缓存），再写入 loadedChildren 与 expandedPaths
    useEffect(() => {
        const pathToExpand = treePath || (filePath ? filePath.split('/').slice(0, -1).join('/') : '');
        const segments = pathToExpand.split('/').filter(Boolean);
        if (segments.length === 0) return;

        let cancelled = false;
        const paths: string[] = [];
        let acc = '';
        for (const seg of segments) {
            const p = acc ? `${acc}/${seg}` : seg;
            paths.push(p);
            acc = p;
        }

        (async () => {
            const results = await Promise.all(paths.map((p) => loadChildren(p)));
            if (cancelled) return;
            const nextLoaded: Record<string, TreeItem[]> = {};
            const nextExpanded: Record<string, true> = {};
            paths.forEach((p, i) => {
                nextLoaded[p] = results[i];
                nextExpanded[p] = true;
            });
            setLoadedChildren((prev) => ({ ...prev, ...nextLoaded }));
            setExpandedPaths((prev) => ({ ...prev, ...nextExpanded }));
        })();
        return () => {
            cancelled = true;
        };
    }, [repoPath, refName, treePath, filePath, loadChildren]);

    const toggleExpand = async (item: TreeItem) => {
        if (item.type !== 'tree') return;
        const key = item.path;
        if (expandedPaths[key]) {
            setExpandedPaths((prev) => {
                const next = { ...prev };
                delete next[key];
                return next;
            });
        } else {
            if (loadedChildren[key]) {
                setExpandedPaths((prev) => ({ ...prev, [key]: true }));
                return;
            }
            const children = await loadChildren(key);
            setLoadedChildren((prev) => ({ ...prev, [key]: children }));
            setExpandedPaths((prev) => ({ ...prev, [key]: true }));
        }
    };

    const handleItemClick = (item: TreeItem) => {
        if (item.type === 'tree') {
            navigate(`/${repoPath}/-/tree/${refToSegment(refName)}/${item.path}`);
        } else {
            navigate(`/${repoPath}/-/blob/${refToSegment(refName)}/${item.path}`);
        }
    };

    const isExpanded = (path: string) => !!expandedPaths[path];
    const isActiveDir = (path: string) => treePath === path || treePath.startsWith(path + '/');
    const isActiveFile = (path: string) => filePath === path;

    if (loading) {
        return (
            <div className="repo-file-tree">
                <div className="repo-file-tree-loading">加载中...</div>
            </div>
        );
    }

    const renderItems = (items: TreeItem[], depth: number) =>
        items.map((item) => {
            const expandedState = item.type === 'tree' && isExpanded(item.path);
            const active = item.type === 'tree' ? isActiveDir(item.path) : isActiveFile(item.path);
            const children = item.type === 'tree' ? loadedChildren[item.path] : null;
            return (
                <div key={item.path} className="repo-file-tree-node">
                    <div
                        className={`repo-file-tree-item ${active ? 'active' : ''}`}
                        style={{ paddingLeft: depth * 12 + 8 }}
                        onClick={() => handleItemClick(item)}
                    >
                        {item.type === 'tree' && (
                            <span
                                className="repo-file-tree-chevron"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(item);
                                }}
                            >
                                {expandedState ? '▼' : '▶'}
                            </span>
                        )}
                        {item.type !== 'tree' && <span className="repo-file-tree-chevron-placeholder" />}
                        <span className={`repo-file-tree-icon ${item.type}`}>
                            {item.type === 'tree' ? '📁' : '📄'}
                        </span>
                        <span className="repo-file-tree-name">{item.name}</span>
                    </div>
                    {item.type === 'tree' && expandedState && children != null && (
                        <div className="repo-file-tree-children">
                            {renderItems(children, depth + 1)}
                        </div>
                    )}
                </div>
            );
        });

    return (
        <div className="repo-file-tree">
            <div className="repo-file-tree-list">
                {renderItems(rootItems, 0)}
            </div>
        </div>
    );
}
